import React, { useState, useEffect, useContext } from "react";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Columns3, Maximize2, CornerDownRight, Gauge, Check, X, Plus, Calendar } from "lucide-react";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, pointerWithin, rectIntersection, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Avatar } from "./BoardCells";
import { BoardMembersContext } from "./boardConstants";
import HScroll from "./HScroll";

const NO_OPT = "__none__";
const byPos = (a, b) => (a.position || 0) - (b.position || 0);
const tintBg = (hex) => { const h = (hex || "#3B82F6").replace("#", ""); return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},0.12)`; };
const fmtDate = (s, loc) => { if (!s) return null; const d = new Date(`${s}T00:00:00`); return isNaN(d.getTime()) ? null : d.toLocaleDateString(loc, { day: "numeric", month: "short" }); };

function ownersOf(item, peopleCol, members) {
  if (!peopleCol) return [];
  const ids = item.values?.[peopleCol.id];
  return Array.isArray(ids) ? ids.map((id) => members.find((m) => m.user_id === id)).filter(Boolean) : [];
}

function CardBody({ item, peopleCol, tag, subCount, dueDate, members, loc }) {
  const owners = ownersOf(item, peopleCol, members);
  const due = fmtDate(dueDate, loc);
  return (
    <>
      {tag && <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md mb-2" style={{ backgroundColor: tintBg(tag.color), color: tag.color }}>{tag.label}</span>}
      <p className="text-sm font-semibold text-slate-900 leading-snug break-words">{item.name}</p>
      <div className="flex items-center gap-3 mt-3">
        {due && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400"><Calendar className="w-3 h-3" />{due}</span>}
        {subCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400"><CornerDownRight className="w-3 h-3" />{subCount}</span>}
        <div className="flex-1" />
        <div className="flex -space-x-1.5">
          {owners.slice(0, 3).map((p) => <span key={p.user_id} className="ring-2 ring-white rounded-full inline-flex"><Avatar person={p} size={22} /></span>)}
        </div>
      </div>
    </>
  );
}

function SortableCard({ item, peopleCol, tag, subCount, dueId, onOpen, loc }) {
  const members = useContext(BoardMembersContext);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} data-testid={`kanban-card-${item.id}`}
      className={cn("group/kc relative bg-white p-3.5 rounded-2xl border border-slate-200 cursor-grab active:cursor-grabbing transition-all",
        isDragging ? "opacity-30" : "shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_28px_rgba(0,0,0,0.12)] hover:-translate-y-0.5")}>
      <button onClick={(e) => { e.stopPropagation(); onOpen(item.id); }} onPointerDown={(e) => e.stopPropagation()} data-testid={`kanban-open-${item.id}`}
        className="absolute top-3 right-3 opacity-0 group-hover/kc:opacity-100 text-slate-300 hover:text-slate-700 transition-all"><Maximize2 className="w-3.5 h-3.5" /></button>
      <CardBody item={item} peopleCol={peopleCol} tag={tag} subCount={subCount} dueDate={item.values?.[dueId]} members={members} loc={loc} />
    </div>
  );
}

function WipPill({ count, wip, color }) {
  const over = wip != null && count > wip;
  return (
    <span data-testid="kanban-wip-btn" className={cn("inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full text-[11px] font-bold transition-colors cursor-pointer", over ? "bg-red-600 text-white" : "bg-white/90")} style={over ? {} : { color }}>
      {count}{wip != null ? `/${wip}` : ""}
    </span>
  );
}

function Column({ option, ids, itemsById, peopleCol, tagCol, dueId, subCountOf, onOpen, wip, onSetWip, onAdd, loc }) {
  const { t } = useApp();
  const { setNodeRef } = useDroppable({ id: option.id });
  const [editWip, setEditWip] = useState(false);
  const [wipVal, setWipVal] = useState(wip ?? "");
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState("");
  useEffect(() => { setWipVal(wip ?? ""); }, [wip]);
  const over = wip != null && ids.length > wip;
  const submitAdd = async () => { const v = addText.trim(); if (!v) { setAdding(false); return; } setAddText(""); await onAdd(v); };
  return (
    <div className="w-[316px] shrink-0 flex flex-col max-h-full" data-testid={`kanban-col-${option.id}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-2xl shadow-sm" style={{ backgroundColor: option.color }}>
        {editWip ? (
          <span className="inline-flex items-center gap-1">
            <input autoFocus type="number" min="0" value={wipVal} onChange={(e) => setWipVal(e.target.value)} data-testid="kanban-wip-input"
              onKeyDown={(e) => { if (e.key === "Enter") { onSetWip(wipVal === "" ? null : Math.max(0, Number(wipVal))); setEditWip(false); } if (e.key === "Escape") setEditWip(false); }}
              className="w-14 text-[11px] px-1.5 py-0.5 rounded-md outline-none text-slate-800" />
            <button onClick={() => { onSetWip(wipVal === "" ? null : Math.max(0, Number(wipVal))); setEditWip(false); }} className="text-white/90"><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => { onSetWip(null); setEditWip(false); }} className="text-white/90"><X className="w-3.5 h-3.5" /></button>
          </span>
        ) : (
          <button onClick={() => setEditWip(true)} title="WIP limit"><WipPill count={ids.length} wip={wip} color={option.color} /></button>
        )}
        <span className="text-sm font-bold text-white tracking-tight truncate flex-1">{option.label}</span>
        {over && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white/90 bg-black/15 px-1.5 py-0.5 rounded"><Gauge className="w-3 h-3" />WIP</span>}
        {option.id !== NO_OPT && <button onClick={() => setAdding(true)} data-testid={`kanban-add-${option.id}`} className="text-white/90 hover:text-white transition-colors"><Plus className="w-4 h-4" /></button>}
      </div>
      <div ref={setNodeRef} className="flex-1 overflow-y-auto mt-2.5 p-1 space-y-2.5">
        {adding && (
          <div className="bg-white p-2.5 rounded-2xl border-2 border-blue-300 shadow-sm">
            <textarea autoFocus rows={2} value={addText} onChange={(e) => setAddText(e.target.value)} placeholder={t("board_add_item") || "New item"} data-testid={`kanban-add-input-${option.id}`}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitAdd(); } if (e.key === "Escape") setAdding(false); }}
              className="w-full text-sm outline-none resize-none text-slate-900 placeholder:text-slate-400" />
            <div className="flex justify-end gap-1.5 mt-1">
              <button onClick={() => setAdding(false)} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1">{t("cancel") || "Cancel"}</button>
              <button onClick={submitAdd} className="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 rounded-md">{t("add") || "Add"}</button>
            </div>
          </div>
        )}
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {ids.map((id) => itemsById[id] && (
            <SortableCard key={id} item={itemsById[id]} peopleCol={peopleCol} dueId={dueId}
              tag={tagCol ? (tagCol.settings?.options || []).find((o) => o.id === itemsById[id].values?.[tagCol.id]) : null}
              subCount={subCountOf(id)} onOpen={onOpen} loc={loc} />
          ))}
        </SortableContext>
        {ids.length === 0 && !adding && <div className="h-16 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center text-[11px] text-slate-300">—</div>}
      </div>
    </div>
  );
}

function buildContainers(board, colId, options) {
  const map = {}; options.forEach((o) => { map[o.id] = []; });
  board.items.filter((i) => !i.parent_id).sort(byPos).forEach((it) => {
    const k = it.values?.[colId] || NO_OPT;
    (map[k] || map[NO_OPT]).push(it.id);
  });
  return map;
}

export default function KanbanView({ board, onSetCell, onReorder, onAddItem, onOpen, boardWip = {}, onSaveWip }) {
  const { t, lang } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const groupCols = board.columns.filter((c) => ["status", "priority", "dropdown"].includes(c.type));
  const [groupColId, setGroupColId] = useState(groupCols[0]?.id || null);
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const col = board.columns.find((c) => c.id === groupColId) || groupCols[0];
  const options = col ? [...(col.settings?.options || []), { id: NO_OPT, label: t("board_no_status"), color: "#94A3B8" }] : [];
  const [containers, setContainers] = useState(() => (col ? buildContainers(board, col.id, options) : {}));
  const wip = (col && boardWip[col.id]) || {};

  useEffect(() => { if (activeId == null && col) setContainers(buildContainers(board, col.id, options)); /* eslint-disable-next-line */ }, [board, col?.id, activeId]);

  if (!groupCols.length) {
    return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-400" data-testid="kanban-empty"><Columns3 className="w-7 h-7 mx-auto mb-3 opacity-50" />{t("board_kanban_no_status")}</div>;
  }

  const peopleCol = board.columns.find((c) => c.type === "people");
  const tagCol = col.type === "priority" ? board.columns.find((c) => c.type === "status") : board.columns.find((c) => c.type === "priority");
  const dueId = board.columns.find((c) => c.id === "due" || c.type === "date")?.id;
  const itemsById = Object.fromEntries(board.items.map((i) => [i.id, i]));
  const subCountOf = (pid) => board.items.filter((i) => i.parent_id === pid).length;
  const findContainer = (id) => (containers[id] ? id : Object.keys(containers).find((k) => containers[k]?.includes(id)));

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const ac = findContainer(active.id), oc = findContainer(over.id);
    if (!ac || !oc || ac === oc) return;
    setContainers((prev) => {
      const a = [...(prev[ac] || [])], o = [...(prev[oc] || [])];
      const ai = a.indexOf(active.id); if (ai < 0) return prev;
      let oi = o.indexOf(over.id); if (oi < 0) oi = o.length;
      a.splice(ai, 1); o.splice(oi, 0, active.id);
      return { ...prev, [ac]: a, [oc]: o };
    });
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const oc = findContainer(over.id); if (!oc) return;
    const next = (() => {
      const items = [...(containers[oc] || [])];
      const ai = items.indexOf(active.id); if (ai < 0) return containers;
      let oi = items.indexOf(over.id); if (over.id === oc || oi < 0) oi = items.length - 1;
      return { ...containers, [oc]: arrayMove(items, ai, oi) };
    })();
    setContainers(next);
    const item = board.items.find((i) => i.id === active.id);
    if (item) { const newVal = oc === NO_OPT ? "" : oc; if ((item.values?.[col.id] || "") !== newVal) onSetCell(item, col.id, newVal); }
    onReorder?.(options.map((o) => o.id).flatMap((k) => next[k] || []));
  };

  const saveWip = (optId, n) => { const nw = { ...wip }; if (n == null) delete nw[optId]; else nw[optId] = n; onSaveWip?.(col.id, nw); };
  const activeItem = activeId ? itemsById[activeId] : null;
  const collision = (args) => { const p = pointerWithin(args); return p.length ? p : rectIntersection(args); };

  return (
    <div data-testid="kanban-view" className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-400">{t("board_group_by")}</span>
        <select value={col.id} onChange={(e) => setGroupColId(e.target.value)} data-testid="kanban-group-select"
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 outline-none cursor-pointer">
          {groupCols.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <DndContext sensors={sensors} collisionDetection={collision}
        onDragStart={(e) => setActiveId(e.active.id)} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
        <HScroll testid="kanban-hscroll" innerClassName="pb-1" step={332}>
          <div className="flex gap-4 items-start" style={{ maxHeight: "calc(100vh - 15rem)" }}>
            {options.map((o) => (
              <Column key={o.id} option={o} ids={containers[o.id] || []} itemsById={itemsById} peopleCol={peopleCol} tagCol={tagCol} dueId={dueId} subCountOf={subCountOf} onOpen={onOpen} loc={loc}
                wip={wip[o.id]} onSetWip={(n) => saveWip(o.id, n)} onAdd={(name) => onAddItem({ [col.id]: o.id }, name)} />
            ))}
          </div>
        </HScroll>
        <DragOverlay>
          {activeItem ? (
            <div className="w-[296px] bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xl rotate-2 ring-2 ring-blue-400">
              <CardBody item={activeItem} peopleCol={peopleCol} dueDate={activeItem.values?.[dueId]}
                tag={tagCol ? (tagCol.settings?.options || []).find((o) => o.id === activeItem.values?.[tagCol.id]) : null}
                subCount={subCountOf(activeItem.id)} members={[]} loc={loc} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
